/*
 * 版权所有 (c) 华为技术有限公司 2024
 * Copyright (c) Huawei Technologies Co., Ltd. 2024. All rights reserved.
 */
import { Injectable } from '@angular/core';
import {
    type Filter,
    type HttpResponse,
    type Polling,
    type Rest,
    type RestJob,
    DownloadService,
    ERROR_OK,
    HttpService,
    I18nService,
    LocalStorageService,
    PollingService,
    TimeService,
    UNPLACEHOLDER,
    UtilService,
} from '@dm/core';
import { cloneDeep } from 'lodash';
import { Observable } from 'rxjs';

import { STORAGE_KEY } from '../../../common/constants';
import { AlarmsAndEventsStrService } from './alarms-and-events-str.service';

@Injectable()
export class AlarmsAndEventsService {
    constructor(
        private util: UtilService,
        private polling: PollingService,
        private http: HttpService,
        private time: TimeService,
        private downloadService: DownloadService,
        private localStorageService: LocalStorageService,
        private str: AlarmsAndEventsStrService,
        private i18nService: I18nService,
    ) {
    }

    // 获取告警类型
    getObjectType(): Observable<HttpResponse> {
        const rest = this.http.makeUrl('alarm.objectDefinition').setParams({ language: this.i18nService.language === 'zh' ? 2 : 1 });
        return this.http.get(rest);
    }

    removePolling(p: Polling): void {
        this.polling.remove(p);
    }

    getEventOrAlarm(id, url): Observable<HttpResponse> {
        const rest = this.http.makeUrl(url).setParams({
            range: '[0-1]',
            timeConversion: 0,
        }).setFilters([{ field: 'sequence', matchMode: '::', value: id }]);
        return this.http.get(rest);
    }
    pollData(dataRest, countRest): Polling {
        const restJobs: RestJob[] = [
            {
                rest: dataRest,
                method: 'GET',
            },
            {
                rest: countRest,
                method: 'GET',
            },
        ];

        return this.polling.addCombo(restJobs, 15);
    }

    getCurrentAlarmRest(params): Rest {
        return this.http.makeUrl('alarm.getCurrentAlarm').setParams(params);
    }

    getAlarmCountRest(params): Rest {
        return this.http.makeUrl('alarm.getAlarmCount').setParams(params);
    }

    getAlarmRest(dataQueryMo, countQueryMo): Rest[] {
        return [
            this.http.makeUrl('alarm.getCurrentAlarm').setParams(dataQueryMo),
            this.http.makeUrl('alarm.getAlarmCount').setParams(countQueryMo),
        ];
    }

    getEventRest(dataQueryMo, countQueryMo): Rest[] {
        return [
            this.http.makeUrl('alarm.getAllEvent').setParams(dataQueryMo),
            this.http.makeUrl('alarm.getAllEventCount').setParams(countQueryMo),
        ];
    }

    alarmRest(): Rest {
        return this.http.makeUrl('alarm.getCurrentAlarm');
    }

    eventRest(): Rest {
        return this.http.makeUrl('alarm.getAllEvent');
    }

    batchEvent(filters: Filter[]): Observable<HttpResponse> {
        const rest = this.eventRest().setFilters(filters).setRange({ limit: 100, offset: 0 }).setParams({ timeConversion: 0 });
        return this.http.get(rest);
    }

    // 告警声音设置cookies
    setAlarmsSettings(alarmVoiceEnabled, minAlarmLevel): void {
        this.localStorageService.set(STORAGE_KEY.ALARM_VOICE_ENABLED, alarmVoiceEnabled);
        this.localStorageService.set(STORAGE_KEY.MIN_ALARM_LEVEL, minAlarmLevel);
    }

    // 告警设置获取cookies
    getAlarmsSettings(): any[] {
        return [
            this.localStorageService.get(STORAGE_KEY.ALARM_VOICE_ENABLED),
            this.localStorageService.get(STORAGE_KEY.MIN_ALARM_LEVEL),
        ];
    }

    // 清除告警
    clearAlarms(sequence): Observable<HttpResponse> {
        const rest = this.http
            .makeUrl('alarm.clearAlarm')
            .setParams({ sequence });
        return this.http.delete(rest, { doException: false });
    }

    // 发送模拟告警
    postSimulationAlarms(info): Observable<HttpResponse> {
        const rest = this.http.makeUrl('alarm.testAlarm');
        return this.http.post(rest, info);
    }

    // 获取智能告警列表
    getSmartfixalarm(): Observable<HttpResponse> {
        const rest = this.http.makeUrl('smartgui.getSmartfixalarm', 'V2');
        return this.http.get(rest);
    }

    // 获取系统信息
    getSystemInfo(): Observable<HttpResponse> {
        const rest = this.http.makeUrl('system.get');
        return this.http.get(rest);
    }

    // 导出所选数据
    exportSelected(type: 'alarm' | 'event', dataTable): void {
        const headsExt = dataTable.columns.map((col) => {
            const obj = {
                head: col.header,
                property: col.field,
            };
            return obj;
        });

        // 导出数据中补充"详情、建议"两个字段
        const detailsHeader = type === 'alarm'
            ? this.str.detailsLabelText.alarmHeader
            : this.str.detailsLabelText.eventHeafer;

        headsExt.push({
            head: detailsHeader,
            property: 'detail',
        });

        headsExt.push({
            head: this.str.detailsLabelText.suggestion,
            property: 'suggestion',
        });

        // 后面会修改选中数据，做一次深拷贝
        const data = cloneDeep(dataTable.selection);
        const cData = data.map((rowData) => {
            rowData.level = this.str.levelText[rowData.level];

            const expData: any = {};
            Object.keys(rowData).forEach((key) => {
                const valueStr = rowData[key]?.toString();
                expData[key] = this.util.decodeHTML(valueStr);
            });
            return expData;
        });

        this.exportSelectedRequest(type, { cData, headsExt });
    }

    exportSelectedRequest(tab: 'alarm' | 'event', putData: { cData: Array<any>; headsExt: Array<any> }): void {
        const rest = tab === 'alarm'
            ? this.http.makeUrl('alarm.exportSelectedAlarm')
            : this.http.makeUrl('alarm.exportSelectedResource');

        this.http.put(rest, putData).subscribe(
            (res) => {
                rest.setParams({ FileName: res.data.FileName });
                const typeStr = tab === 'alarm' ? 'selectedAlarms' : 'selectedEvents';
                const fileName = this.addTimeStamp(typeStr) + '.xls';
                this.downloadService.download(rest, fileName).subscribe();
            },
        );
    }

    exportAll(tab: 'alarm' | 'event'): Observable<any> {
        return new Observable((observer) => {
            const restKey = tab === 'alarm' ? 'alarm.exportAlarm' : 'alarm.exportResource';
            // 发送导出请求
            this.exportData(restKey).subscribe(
                () => {
                    // 轮询获取下载地址
                    const getPathPolling = this.getExportPath(restKey);
                    getPathPolling.subscribe(
                        (res) => {
                            const { data, result: { code } } = res;
                            // 打包未完成，返回
                            if (code !== ERROR_OK) {
                                return;
                            }

                            // 停止轮询
                            this.removePolling(getPathPolling);
                            // 执行下载
                            const typeStr = tab === 'alarm' ? 'allAlarms' : 'allEvents';
                            const fileName = `${this.addTimeStamp(typeStr)}.tgz`;
                            this.downloadData(restKey, data, fileName);
                            // 返回成功
                            observer.next();
                            // 结束订阅流
                            observer.complete();
                        },
                    );
                    // 轮询出错
                    getPathPolling.err.subscribe(
                        (err) => {
                            // 针对打包未完成，继续轮询
                            // 83887409 打包未完成错误码
                            const { result: { code } } = err;
                            if (code === 83887409) return;
                            this.removePolling(getPathPolling); // 终止轮询
                            observer.error();   // 返回错误
                        },
                    );
                },
                // 发送导出请求出错
                () => {
                    // 返回错误
                    observer.error();
                },
            );
        });
    }

    // 发送打包导出请求
    exportData(restKey: string): Observable<HttpResponse> {
        const rest = this.http.makeUrl(restKey);
        rest.setParams({
            tag: 'package',
            filetype: 1,
        });
        return this.http.get(rest);
    }

    getExportPath(restKey: string): Polling {
        const rest = this.http.makeUrl(restKey);
        rest.setParams({ tag: 'getpath' });
        return this.polling.add(rest, 15);
    }

    // 导出包名加上加时间戳
    addTimeStamp(name: string): string {
        const time = new Date().getTime();
        const timestamp = this.time.moment(time).format('YYYYMMDDHHmmss');
        return name + timestamp;
    }

    // 导出最后一步
    downloadData(restKey: string, path, fileNmae): void {
        const rest = this.http.makeUrl(restKey);
        rest.setParams({ tag: path });
        this.downloadService.download(rest, fileNmae).subscribe();
    }

    // 导出前台生成的CSV文件
    downloadCSV(blob: Blob, fileName: string): void {
        this.downloadService.downloadWithBlob(blob, fileName);
    }

    // 渲染后台加载来的数据
    renderData = (dataArr): any => dataArr.map((data) => {
        const newData = {
            ...data,
            alarmObjType: this.str.objectTypeOpt[data.alarmObjType],
            recoverTime: this.renderTime(data.recoverTime),
            startTime: this.renderTime(data.startTime),
            type: this.str.typeText[data.eventType] || UNPLACEHOLDER,
            alarmStatus: this.str.alarmStatusText[data.alarmStatus],
            description: this.util.decodeHTML(data.description),
            detail: this.util.decodeHTML(data.detail),
            suggestion: this.util.decodeHTML(data.suggestion),
            nativeData: data,
        };

        return newData;
    });

    // 渲染时间戳
    renderTime(time): string {
        return time === 0
            ? UNPLACEHOLDER
            : this.time.formatUTC(time * 1e3);
    }
}
